import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { PopupNames } from 'core';
import { IGroupForLessons, usePopupStateOperations } from 'core/hooks';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface IScheduleCardProps {
  group: IGroupForLessons;
}

const ZoomAccountLine = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: ${GroupColors.textSecondary};
`;

const ZoomAccountLabel = styled.strong`
  color: ${GroupColors.textPrimary};
`;

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const weekDayLabel = (weekDay: number | null | undefined): string => {
  // Backend convention: 1..7 (Mon..Sun) — same as moment's `isoWeekday`.
  if (weekDay == null) return '—';
  const idx = (((Number(weekDay) - 1) % 7) + 7) % 7;
  return WEEKDAY_KEYS[idx];
};

/**
 * «Расписание группы» snapshot card on ScheduleTab. Shows the weekly
 * pattern (`classesDays`) + the bound Zoom account, with an «Edit»
 * shortcut that opens the existing `editGroupPopup` (where slots are
 * actually edited today). When the auto-process slots live (Zoom
 * meeting recurrence — `zoomSchedules`), they're surfaced on ZoomTab.
 */
export const ScheduleCard = memo(({ group }: IScheduleCardProps) => {
  const { t } = useTranslation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const days = useMemo(() => (group.classesDays || []).filter(Boolean), [
    group.classesDays,
  ]);

  const onEdit = useCallback(() => {
    showPopup(PopupNames.editGroupPopup, group.id);
  }, [showPopup, group.id]);

  return (
    <S.Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <S.CardTitle style={{ margin: 0 }}>
          {t('pages.groupDetail.schedule.cardTitle')}
        </S.CardTitle>
        <L.HeaderActions>
          <L.PrimaryActionButton type="button" onClick={onEdit}>
            {t('pages.groupDetail.schedule.edit')}
          </L.PrimaryActionButton>
        </L.HeaderActions>
      </div>
      {days.length === 0 ? (
        <S.SubTitle>{t('pages.groupDetail.schedule.noSlots')}</S.SubTitle>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 10,
          }}
        >
          {days.map((d, i) => (
            <L.Tag
              // eslint-disable-next-line react/no-array-index-key
              key={`${d?.weekDay}-${i}`}
              tone="neutral"
            >
              {t(`pages.groupDetail.schedule.day.${weekDayLabel(d?.weekDay)}`)}
              {d?.startTime ? ` · ${d.startTime}` : ''}
              {d?.endTime ? `–${d.endTime}` : ''}
            </L.Tag>
          ))}
        </div>
      )}
      <ZoomAccountLine>
        <ZoomAccountLabel>
          {t('pages.groupDetail.schedule.zoomAccount')}:{' '}
        </ZoomAccountLabel>
        {group.zoomAccount?.displayName || '—'}
      </ZoomAccountLine>
    </S.Card>
  );
});

export { weekDayLabel };
